/* MetaliQ storefront behaviour.
 *
 * Three independent enhancements, each a no-op when its markup is absent:
 *   1. the mobile navigation toggle
 *   2. the collection sort control
 *   3. the product purchase form and cart drawer handoff
 */
(() => {
  'use strict';

  const routes = (window.MetaliQ && window.MetaliQ.routes) || {};
  const cartAddUrl = routes.cartAdd || '/cart/add';
  const cartChangeUrl = routes.cartChange || '/cart/change';
  const cartUpdateUrl = routes.cartUpdate || '/cart/update';
  const cartUrl = routes.cart || '/cart';
  const checkoutUrl = routes.checkout || '/checkout';

  const customDesignWhatsAppUrl = document.body.dataset.customDesignUrl || '';

  const routeCustomDesignLinksToWhatsApp = (root = document) => {
    if (!customDesignWhatsAppUrl) return;

    root.querySelectorAll('[data-whatsapp-cta], a[href*="/pages/custom-design"]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (href.includes('wa.me/')) return;

      link.href = customDesignWhatsAppUrl;
      link.target = '_blank';
      link.rel = 'noopener';
    });
  };

  routeCustomDesignLinksToWhatsApp();

  // Shopify supplies the policy title; merchant policy content sometimes repeats it as an H1.
  document.querySelectorAll('.shopify-policy__body h1').forEach((heading) => {
    heading.remove();
  });

  /* ----------------------------------------------------------------------
     Cart drawer
     ---------------------------------------------------------------------- */

  let cartDrawerTrigger = null;

  const getCartDrawer = () => document.querySelector('[data-cart-drawer]');

  const renderCartDrawer = (html) => {
    const section = document.querySelector('#shopify-section-cart-drawer');
    if (!section || !html) return false;

    section.innerHTML = html;
    routeCustomDesignLinksToWhatsApp(section);
    syncCartCount();
    return true;
  };

  const cartSectionRequest = () => ({
    sections: ['cart-drawer'],
    sections_url: window.location.pathname,
  });

  const syncCartCount = () => {
    const drawer = getCartDrawer();
    const count = Math.max(0, Number(drawer && drawer.dataset.cartCount) || 0);

    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      const label = trigger.dataset.cartLabel;
      if (label) trigger.setAttribute('aria-label', label.replace('__COUNT__', String(count)));

      let badge = trigger.querySelector('[data-cart-count]');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cart-count';
          badge.dataset.cartCount = '';
          trigger.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
    });
  };

  const refreshCartDrawer = () => {
    const section = document.querySelector('#shopify-section-cart-drawer');
    if (!section) return Promise.reject(new Error('Cart drawer section is unavailable'));

    const separator = cartUrl.includes('?') ? '&' : '?';
    return fetch(`${cartUrl}${separator}section_id=cart-drawer`, {
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart drawer refresh failed');
        return response.text();
      })
      .then((html) => {
        if (!renderCartDrawer(html)) throw new Error('Cart drawer render failed');
      });
  };

  const revealCartDrawer = ({ focus = true } = {}) => {
    const drawer = getCartDrawer();
    if (!drawer) return false;

    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });

    if (focus) {
      window.requestAnimationFrame(() => {
        const panel = drawer.querySelector('.cart-drawer__panel');
        const close = drawer.querySelector('[data-cart-drawer-close]');
        (close || panel)?.focus();
      });
    }
    return true;
  };

  const openCartDrawer = ({ refresh = false, focus = true } = {}) => {
    if (!cartDrawerTrigger) cartDrawerTrigger = document.activeElement;
    if (!revealCartDrawer({ focus })) {
      window.location.assign(cartUrl);
      return Promise.resolve();
    }

    if (!refresh) return Promise.resolve();
    return refreshCartDrawer()
      .then(() => revealCartDrawer({ focus: false }))
      .catch(() => {
        // Keep the already-rendered drawer usable if a background refresh fails.
      });
  };

  const closeCartDrawer = () => {
    const drawer = getCartDrawer();
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (cartDrawerTrigger instanceof HTMLElement) cartDrawerTrigger.focus();
    cartDrawerTrigger = null;
  };

  const updateCartDrawerQuantity = (button) => {
    const drawer = getCartDrawer();
    const key = button.dataset.key;
    const requestedQuantity = Number(button.dataset.quantity) || 0;
    const quantity = button.hasAttribute('data-cart-drawer-remove') ? 0 : Math.max(1, requestedQuantity);
    if (!drawer || !key || drawer.getAttribute('aria-busy') === 'true') return;

    const updates = { [key]: quantity };
    const linkedKey = button.dataset.linkedKey;
    const linkedRatio = Math.max(0, Number(button.dataset.linkedRatio) || 0);
    if (linkedKey) updates[linkedKey] = quantity * linkedRatio;

    drawer.setAttribute('aria-busy', 'true');
    const item = button.closest('[data-cart-drawer-item]');
    const quantityOutput = item && item.querySelector('.cart-drawer__quantity span');
    const previousQuantity = Math.max(0, Number(quantityOutput && quantityOutput.textContent) || 0);
    drawer.dataset.cartCount = String(Math.max(0, Number(drawer.dataset.cartCount) + quantity - previousQuantity));
    if (quantityOutput && quantity > 0) quantityOutput.textContent = String(quantity);
    if (item && quantity === 0) item.classList.add('is-removing');
    syncCartCount();

    fetch(cartUpdateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...cartSectionRequest() }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then((cart) => {
        const html = cart.sections && cart.sections['cart-drawer'];
        if (renderCartDrawer(html)) return undefined;
        return refreshCartDrawer();
      })
      .then(() => revealCartDrawer({ focus: false }))
      .catch(() => window.location.assign(cartUrl));
  };

  const setCartDiscountStatus = (form, message, state) => {
    const status = form && form.querySelector('[data-cart-discount-status]');
    if (!status) return;

    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('is-success', state === 'success');
    status.classList.toggle('is-error', state === 'error');
  };

  const applyCartDiscount = (form) => {
    const drawer = getCartDrawer();
    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('[data-cart-discount-button]');
    const code = input && input.value.trim();
    if (!drawer || !input || !button || drawer.getAttribute('aria-busy') === 'true') return;

    if (!code) {
      setCartDiscountStatus(form, form.dataset.empty, 'error');
      input.focus();
      return;
    }

    const defaultLabel = button.dataset.defaultLabel || button.textContent;
    drawer.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.textContent = form.dataset.applying || 'Applying...';
    setCartDiscountStatus(form, '', '');

    fetch(cartUpdateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code, ...cartSectionRequest() }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Discount update failed');
        return response.json();
      })
      .then((cart) => {
        const matchedCode = Array.isArray(cart.discount_codes)
          ? cart.discount_codes.find((discount) => discount.code.toLowerCase() === code.toLowerCase())
          : null;
        if (matchedCode && matchedCode.applicable === false) throw new Error('Discount is not applicable');

        const html = cart.sections && cart.sections['cart-drawer'];
        if (renderCartDrawer(html)) return undefined;
        return refreshCartDrawer();
      })
      .then(() => {
        revealCartDrawer({ focus: false });
        const updatedForm = document.querySelector('[data-cart-discount-form]');
        const updatedInput = updatedForm && updatedForm.querySelector('input[name="discount"]');
        /* The drawer re-renders, so reopen the collapsed panel to show the result. */
        const updatedDetails = updatedForm && updatedForm.closest('[data-cart-discount-details]');
        if (updatedDetails) updatedDetails.open = true;
        if (updatedInput) updatedInput.value = code;
        if (updatedForm) setCartDiscountStatus(updatedForm, updatedForm.dataset.success, 'success');
      })
      .catch(() => {
        const activeForm = document.querySelector('[data-cart-discount-form]') || form;
        const activeDetails = activeForm.closest('[data-cart-discount-details]');
        if (activeDetails) activeDetails.open = true;
        setCartDiscountStatus(activeForm, activeForm.dataset.error, 'error');
      })
      .finally(() => {
        const activeDrawer = getCartDrawer();
        const activeButton = document.querySelector('[data-cart-discount-button]') || button;
        if (activeDrawer) activeDrawer.removeAttribute('aria-busy');
        activeButton.disabled = false;
        activeButton.textContent = activeButton.dataset.defaultLabel || defaultLabel;
      });
  };

  window.MetaliQCartDrawer = {
    open: openCartDrawer,
    refresh: refreshCartDrawer,
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (trigger) {
      event.preventDefault();
      cartDrawerTrigger = trigger;
      openCartDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeCartDrawer();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-drawer-quantity]');
    if (quantityButton) {
      event.preventDefault();
      updateCartDrawerQuantity(quantityButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getCartDrawer();
    if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeCartDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const panel = drawer.querySelector('.cart-drawer__panel');
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      applyCartDiscount(discountForm);
      return;
    }

    const form = event.target.closest('[data-card-add-form]');
    if (!form) return;

    event.preventDefault();
    const addButton = form.querySelector('[data-card-add-button]');
    const buyNowButton = form.querySelector('[data-card-buy-now]');
    const addLabel = form.querySelector('[data-card-add-label]');
    const buyNowLabel = form.querySelector('[data-card-buy-label]');
    const error = form.querySelector('[data-card-add-error]');
    const variantId = Number(form.elements.id && form.elements.id.value);
    const isBuyNow = event.submitter?.hasAttribute('data-card-buy-now');
    const activeButton = isBuyNow ? buyNowButton : addButton;
    const activeLabel = isBuyNow ? buyNowLabel : addLabel;
    const originalAddLabel = addLabel && addLabel.textContent;
    const originalBuyNowLabel = buyNowLabel && buyNowLabel.textContent;
    if (!activeButton || !variantId || activeButton.disabled) return;

    if (!isBuyNow) cartDrawerTrigger = addButton;
    if (addButton) addButton.disabled = true;
    if (buyNowButton) buyNowButton.disabled = true;
    activeButton.setAttribute('aria-busy', 'true');
    if (activeLabel) activeLabel.textContent = activeButton.dataset.buying || activeButton.dataset.adding || activeLabel.textContent;
    if (error) error.hidden = true;

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        ...(isBuyNow ? {} : cartSectionRequest()),
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then((cart) => {
        if (isBuyNow) {
          window.location.assign(checkoutUrl);
          return undefined;
        }
        const html = cart.sections && cart.sections['cart-drawer'];
        if (renderCartDrawer(html)) return openCartDrawer();
        return refreshCartDrawer().then(() => openCartDrawer());
      })
      .catch(() => {
        if (error) error.hidden = false;
      })
      .finally(() => {
        if (addButton) addButton.disabled = false;
        if (buyNowButton) buyNowButton.disabled = false;
        activeButton.removeAttribute('aria-busy');
        if (addLabel) addLabel.textContent = originalAddLabel;
        if (buyNowLabel) buyNowLabel.textContent = originalBuyNowLabel;
      });
  });

  document.querySelectorAll('[data-announcement-slider]').forEach((slider) => {
    const slides = Array.from(slider.querySelectorAll('[data-announcement-slide]'));
    const previous = slider.querySelector('[data-announcement-previous]');
    const next = slider.querySelector('[data-announcement-next]');
    if (slides.length < 2 || !previous || !next) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let rotationTimer;

    const showSlide = (index) => {
      const current = slides[activeIndex];
      const targetIndex = (index + slides.length) % slides.length;
      if (targetIndex === activeIndex) return;

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      activeIndex = targetIndex;
      slides[activeIndex].classList.add('is-active');
      slides[activeIndex].setAttribute('aria-hidden', 'false');
      window.setTimeout(() => current.classList.remove('is-leaving'), 350);
    };

    const stopRotation = () => window.clearInterval(rotationTimer);
    const startRotation = () => {
      stopRotation();
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        rotationTimer = window.setInterval(() => showSlide(activeIndex + 1), 5000);
      }
    };

    previous.addEventListener('click', () => {
      showSlide(activeIndex - 1);
      startRotation();
    });
    next.addEventListener('click', () => {
      showSlide(activeIndex + 1);
      startRotation();
    });
    slider.addEventListener('mouseenter', stopRotation);
    slider.addEventListener('mouseleave', startRotation);
    slider.addEventListener('focusin', stopRotation);
    slider.addEventListener('focusout', startRotation);
    startRotation();
  });

  document.querySelectorAll('[data-testimonial-slider]').forEach((slider) => {
    const slides = Array.from(slider.querySelectorAll('[data-testimonial-slide]'));
    const previous = slider.querySelector('[data-testimonial-previous]');
    const next = slider.querySelector('[data-testimonial-next]');
    if (slides.length < 2 || !previous || !next) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const fitQuotes = () => {
      slides.forEach((slide) => {
        const quote = slide.querySelector('blockquote');
        if (!quote) return;

        quote.style.removeProperty('--quote-fit-size');
        let fontSize = parseFloat(window.getComputedStyle(quote).fontSize);
        const minimumSize = 17;

        while (slide.scrollHeight > slide.clientHeight && fontSize > minimumSize) {
          fontSize -= 1;
          quote.style.setProperty('--quote-fit-size', `${fontSize}px`);
        }
      });
    };

    const showTestimonial = (index) => {
      const current = slides[activeIndex];
      const targetIndex = (index + slides.length) % slides.length;
      if (targetIndex === activeIndex) return;

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      activeIndex = targetIndex;
      slides[activeIndex].classList.add('is-active');
      slides[activeIndex].setAttribute('aria-hidden', 'false');
      window.setTimeout(() => current.classList.remove('is-leaving'), 400);
    };

    previous.addEventListener('click', () => showTestimonial(activeIndex - 1));
    next.addEventListener('click', () => showTestimonial(activeIndex + 1));

    let fitTimer;
    const scheduleFit = () => {
      window.clearTimeout(fitTimer);
      fitTimer = window.setTimeout(fitQuotes, 100);
    };
    window.addEventListener('resize', scheduleFit, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitQuotes);
    } else {
      fitQuotes();
    }
  });

  document.querySelectorAll('[data-faq-accordion]').forEach((accordion) => {
    const items = Array.from(accordion.querySelectorAll('details'));

    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((otherItem) => {
          if (otherItem !== item) otherItem.open = false;
        });
      });
    });
  });

  document.querySelectorAll('[data-whatsapp-widget]').forEach((widget) => {
    const prompt = widget.querySelector('[data-whatsapp-prompt]');
    const close = widget.querySelector('[data-whatsapp-close]');
    const storageKey = 'metaliq-whatsapp-prompt-hidden';
    const seenKey = 'metaliq-whatsapp-prompt-seen';
    if (!prompt) return;

    let shouldShow = true;
    try {
      shouldShow = window.sessionStorage.getItem(storageKey) !== 'true' && window.sessionStorage.getItem(seenKey) !== 'true';
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }

    if (!shouldShow) {
      prompt.hidden = true;
      return;
    }

    const hidePrompt = () => {
      widget.classList.remove('is-ready', 'has-open-prompt');
      window.setTimeout(() => {
        prompt.hidden = true;
      }, 350);
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {
        // The prompt still closes for this render when storage is unavailable.
      }
    };

    window.setTimeout(() => {
      widget.classList.add('is-ready');
      widget.classList.add('has-open-prompt');
      try {
        window.sessionStorage.setItem(seenKey, 'true');
      } catch (error) {
        // Showing the prompt does not depend on storage availability.
      }
      window.setTimeout(hidePrompt, 9000);
    }, 2500);

    if (close) {
      close.addEventListener('click', hidePrompt);
    }
  });

  document.querySelectorAll('[data-hero-reel]').forEach((reel) => {
    const track = reel.querySelector('.home-hero__track');
    const firstGroup = track && track.querySelector('.home-hero__group');
    const mobileLayout = window.matchMedia('(max-width: 980px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!track || !firstGroup) return;

    let frame;
    let previousTime;
    let paused = false;
    let resumeTimer;
    const speed = 32;

    const loop = (time) => {
      if (!mobileLayout.matches || reducedMotion.matches || paused) {
        previousTime = time;
        frame = window.requestAnimationFrame(loop);
        return;
      }

      const elapsed = Math.min(40, time - (previousTime || time));
      previousTime = time;
      reel.scrollLeft += speed * (elapsed / 1000);

      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const cycleWidth = firstGroup.getBoundingClientRect().width + gap;
      if (cycleWidth > 0 && reel.scrollLeft >= cycleWidth) reel.scrollLeft -= cycleWidth;
      frame = window.requestAnimationFrame(loop);
    };

    const pause = () => {
      paused = true;
      window.clearTimeout(resumeTimer);
    };
    const resume = (delay = 700) => {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        paused = false;
        previousTime = undefined;
      }, delay);
    };

    reel.addEventListener('pointerdown', pause);
    reel.addEventListener('pointerup', () => resume());
    reel.addEventListener('pointercancel', () => resume());
    reel.addEventListener('mouseenter', pause);
    reel.addEventListener('mouseleave', () => resume(0));
    reel.addEventListener('wheel', () => {
      pause();
      resume();
    }, { passive: true });
    reel.addEventListener('focusin', pause);
    reel.addEventListener('focusout', () => resume());
    mobileLayout.addEventListener('change', () => {
      reel.scrollLeft = 0;
      previousTime = undefined;
    });

    frame = window.requestAnimationFrame(loop);
    window.addEventListener('pagehide', () => window.cancelAnimationFrame(frame), { once: true });
  });

  /* ----------------------------------------------------------------------
     Money
     ---------------------------------------------------------------------- */

  /** Format an amount in minor units using the store's Liquid money format. */
  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return template;

    const withDelimiters = (value, precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(amount, 2, "'", '.');
        break;
      default:
        value = withDelimiters(amount, 2, ',', '.');
    }

    return template.replace(match[0], value);
  };

  /* ----------------------------------------------------------------------
     1. Mobile navigation
     ---------------------------------------------------------------------- */

  const menuButton = document.querySelector('[data-nav-toggle]');
  const navigation = document.querySelector('[data-nav]');

  if (menuButton && navigation) {
    const sizeMobileMenu = () => {
      const siteHeader = navigation.closest('[data-site-header]');
      if (!siteHeader) return;
      const availableHeight = Math.max(240, window.innerHeight - siteHeader.getBoundingClientRect().bottom);
      navigation.style.setProperty('--mobile-menu-height', `${availableHeight}px`);
    };

    const setMenu = (open) => {
      if (open) sizeMobileMenu();
      menuButton.setAttribute('aria-expanded', String(open));
      navigation.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
      if (!open) {
        navigation.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      }
    };

    menuButton.addEventListener('click', () => {
      setMenu(menuButton.getAttribute('aria-expanded') !== 'true');
    });

    window.addEventListener('resize', () => {
      if (menuButton.getAttribute('aria-expanded') === 'true') sizeMobileMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menuButton.getAttribute('aria-expanded') === 'true') {
        setMenu(false);
        menuButton.focus();
      }
    });

    navigation.addEventListener('click', (event) => {
      if (event.target.closest('a')) setMenu(false);
    });

    // The nav returns to its horizontal desktop layout at 1100px, where
    // `is-open` means nothing — without this the body stays scroll-locked.
    const desktop = window.matchMedia('(min-width: 1100px)');
    const syncToViewport = (event) => {
      if (event.matches) setMenu(false);
    };
    if (desktop.addEventListener) {
      desktop.addEventListener('change', syncToViewport);
    } else {
      desktop.addListener(syncToViewport);
    }
  }

  document.querySelectorAll('.nav-dropdown').forEach((dropdown) => {
    const isDesktopNavigation = () => window.innerWidth >= 1100;
    let closeTimer;

    dropdown.addEventListener('mouseenter', () => {
      window.clearTimeout(closeTimer);
      if (isDesktopNavigation()) dropdown.setAttribute('open', '');
    });
    dropdown.addEventListener('mouseleave', () => {
      if (isDesktopNavigation()) {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => dropdown.removeAttribute('open'), 220);
      }
    });
    dropdown.addEventListener('focusin', () => {
      window.clearTimeout(closeTimer);
      if (isDesktopNavigation()) dropdown.setAttribute('open', '');
    });
    dropdown.addEventListener('focusout', (event) => {
      if (isDesktopNavigation() && !dropdown.contains(event.relatedTarget)) {
        dropdown.removeAttribute('open');
      }
    });
    document.addEventListener('click', (event) => {
      if (!dropdown.contains(event.target)) dropdown.removeAttribute('open');
    });
    dropdown.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        dropdown.removeAttribute('open');
        dropdown.querySelector('summary')?.focus();
      }
    });
  });

  /* ----------------------------------------------------------------------
     Header search overlay and predictive results
     ---------------------------------------------------------------------- */

  const searchDialog = document.querySelector('[data-search-dialog]');
  const searchTrigger = document.querySelector('[data-search-open]');
  const searchClose = searchDialog?.querySelector('[data-search-close]');
  const searchForm = searchDialog?.querySelector('[data-search-overlay-form]');
  const searchInput = searchDialog?.querySelector('[data-search-input]');
  const searchResults = searchDialog?.querySelector('[data-search-results]');

  if (searchDialog && searchTrigger && searchClose && searchForm && searchInput && searchResults) {
    const initialResultsMarkup = searchResults.innerHTML;
    let searchTimer;
    let searchRequest;

    const setSearchExpanded = (expanded) => {
      searchTrigger.setAttribute('aria-expanded', String(expanded));
      searchInput.setAttribute('aria-expanded', String(expanded && searchInput.value.trim().length >= 2));
    };

    const closeSearch = () => {
      if (!searchDialog.open) return;
      searchRequest?.abort();
      searchDialog.close();
    };

    const openSearch = (event) => {
      event.preventDefault();
      if (menuButton?.getAttribute('aria-expanded') === 'true') menuButton.click();
      if (!searchDialog.open) searchDialog.showModal();
      document.documentElement.classList.add('search-overlay-open');
      setSearchExpanded(true);
      window.requestAnimationFrame(() => searchInput.focus());
    };

    const resetSearchResults = () => {
      searchRequest?.abort();
      searchResults.removeAttribute('aria-busy');
      searchResults.innerHTML = initialResultsMarkup;
      searchInput.setAttribute('aria-expanded', 'false');
    };

    const renderPredictiveResults = async (query) => {
      searchRequest?.abort();
      const request = new AbortController();
      searchRequest = request;
      searchResults.setAttribute('aria-busy', 'true');

      const url = new URL(searchDialog.dataset.predictiveUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'predictive-search');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: request.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        const markup = await response.text();
        if (searchRequest !== request) return;
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        searchResults.innerHTML = section ? section.innerHTML : markup;
        searchInput.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError') {
          searchResults.innerHTML = initialResultsMarkup;
          searchInput.setAttribute('aria-expanded', 'false');
        }
      } finally {
        if (searchRequest === request) searchResults.removeAttribute('aria-busy');
      }
    };

    searchTrigger.addEventListener('click', openSearch);
    searchClose.addEventListener('click', closeSearch);
    searchDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSearch();
    });
    searchDialog.addEventListener('click', (event) => {
      if (event.target === searchDialog) closeSearch();
    });
    searchDialog.addEventListener('close', () => {
      window.clearTimeout(searchTimer);
      searchRequest?.abort();
      document.documentElement.classList.remove('search-overlay-open');
      setSearchExpanded(false);
      searchTrigger.focus();
    });

    searchInput.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        resetSearchResults();
        return;
      }
      searchTimer = window.setTimeout(() => renderPredictiveResults(query), 220);
    });

    searchForm.addEventListener('submit', (event) => {
      if (searchInput.value.trim()) return;
      event.preventDefault();
      searchInput.focus();
    });
  }

  /* ----------------------------------------------------------------------
     2. Collection sorting
     ---------------------------------------------------------------------- */

  const sortControl = document.querySelector('[data-sort-by]');
  if (sortControl) {
    sortControl.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  const filterDialog = document.querySelector('[data-filter-dialog]');
  const filterOpen = document.querySelector('[data-filter-open]');
  const filterClose = filterDialog?.querySelector('[data-filter-close]');
  if (filterDialog && filterOpen && filterClose) {
    const closeFilterDialog = () => {
      filterDialog.close();
      document.documentElement.classList.remove('filter-drawer-open');
    };

    filterOpen.addEventListener('click', () => {
      filterDialog.showModal();
      document.documentElement.classList.add('filter-drawer-open');
    });
    filterClose.addEventListener('click', closeFilterDialog);
    filterDialog.addEventListener('close', () => {
      document.documentElement.classList.remove('filter-drawer-open');
    });
    filterDialog.addEventListener('click', (event) => {
      if (event.target === filterDialog) closeFilterDialog();
    });
  }

  document.querySelectorAll('[data-filter-auto-apply]').forEach((form) => {
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', () => form.requestSubmit());
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    // Tall galleries scroll into view before sticking; never shrink the artwork.
    const updateGalleryOffset = () => {
      const headerHeight = document.querySelector('[data-site-header]')?.getBoundingClientRect().height || 76;
      const offset = Math.min(headerHeight + 16, window.innerHeight - gallery.getBoundingClientRect().height - 16);
      gallery.style.setProperty('--gallery-sticky-top', `${offset}px`);
    };
    const galleryObserver = new ResizeObserver(updateGalleryOffset);
    galleryObserver.observe(gallery);
    window.addEventListener('resize', updateGalleryOffset);
    updateGalleryOffset();
    const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));
    const mediaItems = Array.from(gallery.querySelectorAll('[data-gallery-media]'));
    const zoomDialog = gallery.querySelector('[data-product-zoom-dialog]');
    const zoomImage = gallery.querySelector('[data-product-zoom-image]');
    const zoomClose = gallery.querySelector('[data-product-zoom-close]');
    let zoomTrigger = null;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const targetId = thumbnail.dataset.galleryThumbnail;
        thumbnails.forEach((item) => {
          const active = item === thumbnail;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        mediaItems.forEach((item) => {
          const active = item.dataset.galleryMedia === targetId;
          item.hidden = !active;
          if (!active) item.querySelector('video')?.pause();
        });
      });
    });

    gallery.querySelectorAll('[data-product-zoom]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!zoomDialog || !zoomImage) return;
        zoomTrigger = trigger;
        zoomImage.src = trigger.dataset.zoomSrc || '';
        zoomImage.alt = trigger.dataset.zoomAlt || '';
        zoomDialog.showModal();
      });
    });

    const closeZoom = () => {
      if (!zoomDialog || !zoomDialog.open) return;
      zoomDialog.close();
    };

    zoomClose?.addEventListener('click', closeZoom);
    zoomDialog?.addEventListener('click', (event) => {
      if (event.target === zoomDialog) closeZoom();
    });
    zoomDialog?.addEventListener('close', () => {
      zoomImage?.removeAttribute('src');
      zoomTrigger?.focus();
    });
  });

  document.querySelectorAll('[data-product-tabs]').forEach((tabsRoot) => {
    const tabs = Array.from(tabsRoot.querySelectorAll('[data-product-tab]'));
    const panels = Array.from(tabsRoot.querySelectorAll('[data-product-panel]'));

    const activate = (tab) => {
      const target = tab.dataset.productTab;
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.productPanel !== target;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(index + direction + tabs.length) % tabs.length];
        activate(next);
        next.focus();
      });
    });
  });

  /* ----------------------------------------------------------------------
     3. Product purchase form
     ---------------------------------------------------------------------- */

  document.querySelectorAll('[data-product-purchase]').forEach((root) => {
    const form = root.querySelector('.product-form');
    const priceOutput = root.querySelector('[data-product-price]');
    if (!form || !priceOutput) return;

    const variantInputs = Array.from(root.querySelectorAll('[data-variant-input]'));
    const variantIdInput = root.querySelector('[data-variant-id]');
    const currentPriceOutput = priceOutput.querySelector('[data-current-price]');
    const comparePriceOutput = priceOutput.querySelector('[data-compare-price]');
    const salePercentageOutput = priceOutput.querySelector('[data-sale-percentage]');
    const addButton = root.querySelector('[data-add-button]');
    const buyNowButton = root.querySelector('[data-buy-now]');
    const addButtonLabel = root.querySelector('[data-product-add-label]');
    const buyNowButtonLabel = root.querySelector('[data-product-buy-label]');
    const addError = root.querySelector('[data-add-error]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    root.querySelectorAll('[data-product-quantity]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput) return;
        const quantity = Math.max(1, Math.floor(Number(quantityInput.value) || 1));
        quantityInput.value = String(Math.max(1, quantity + Number(button.dataset.productQuantity)));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    const salesNotice = root.querySelector('[data-sales-expires]');
    if (salesNotice) {
      const remaining = Number(salesNotice.dataset.salesExpires) * 1000 - Date.now();
      if (remaining <= 0) salesNotice.hidden = true;
      else window.setTimeout(() => { salesNotice.hidden = true; }, remaining);
    }
    const selectedSize = root.querySelector('[data-selected-size]');
    const availability = root.querySelector('[data-product-availability]');
    const availabilityText = root.querySelector('[data-availability-text]');
    const stickyBar = root.querySelector('[data-product-sticky-atc]');
    const stickyButton = root.querySelector('[data-sticky-add]');
    const stickyBuyNowButton = root.querySelector('[data-sticky-buy-now]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickySize = root.querySelector('[data-sticky-size]');

    const selectedVariant = () => variantInputs.find((input) => input.checked);
    const variantId = () => selectedVariant()?.value || (variantIdInput && variantIdInput.value);
    const variantPrice = () => {
      if (selectedVariant()) return Number(selectedVariant().dataset.price);
      return Number(priceOutput.dataset.basePrice) || 0;
    };
    const variantComparePrice = () => {
      if (selectedVariant()) return Number(selectedVariant().dataset.comparePrice) || 0;
      return Number(priceOutput.dataset.baseComparePrice) || 0;
    };

    const update = () => {
      const variant = selectedVariant();
      const price = variantPrice();
      const comparePrice = variantComparePrice();
      const available = variant ? variant.dataset.available === 'true' : !addButton?.disabled;
      if (currentPriceOutput) currentPriceOutput.textContent = formatMoney(price, root.dataset.moneyFormat);
      if (comparePriceOutput) {
        comparePriceOutput.textContent = formatMoney(comparePrice, root.dataset.moneyFormat);
        comparePriceOutput.hidden = comparePrice <= price;
      }
      if (salePercentageOutput) {
        const percentage = comparePrice > price ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
        salePercentageOutput.textContent = (root.dataset.saleLabel || '__PERCENT__% off').replace('__PERCENT__', percentage);
        salePercentageOutput.hidden = comparePrice <= price;
      }
      if (selectedSize && variant) selectedSize.textContent = variant.dataset.variantTitle || '';
      if (variantIdInput && variant && variantIdInput.value !== variant.value) {
        variantIdInput.value = variant.value;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (stickySize && variant) stickySize.textContent = variant.dataset.variantTitle || '';
      if (stickyPrice) stickyPrice.textContent = formatMoney(price, root.dataset.moneyFormat);
      if (availability) availability.classList.toggle('is-unavailable', !available);
      if (availabilityText) {
        availabilityText.textContent = available ? root.dataset.availableLabel : root.dataset.soldOutLabel;
      }
      if (addButton) {
        addButton.disabled = !available;
        addButton.setAttribute('aria-label', available ? root.dataset.addLabel : root.dataset.soldOutLabel);
      }
      if (addButtonLabel) addButtonLabel.textContent = available ? root.dataset.addLabel : root.dataset.soldOutLabel;
      if (buyNowButton) {
        buyNowButton.disabled = !available;
      }
      if (buyNowButtonLabel) buyNowButtonLabel.textContent = available ? root.dataset.buyLabel : root.dataset.soldOutLabel;
      if (stickyButton) {
        stickyButton.disabled = !available;
        stickyButton.textContent = available ? root.dataset.addLabel : root.dataset.soldOutLabel;
      }
      if (stickyBuyNowButton) {
        stickyBuyNowButton.disabled = !available;
        stickyBuyNowButton.textContent = available ? root.dataset.buyLabel : root.dataset.soldOutLabel;
      }
      if (addError) addError.hidden = true;
    };

    variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const url = new URL(root.dataset.productUrl || window.location.pathname, window.location.origin);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', `${url.pathname}${url.search}`);
        update();
      });
    });

    stickyButton?.addEventListener('click', () => {
      if (addButton?.disabled) return;
      form.requestSubmit(addButton);
    });
    stickyBuyNowButton?.addEventListener('click', () => {
      if (buyNowButton?.disabled) return;
      buyNowButton?.click();
    });

    // Delegate to the app-owned trigger so Releasit retains its configured order flow.
    const codError = root.querySelector('[data-cod-error]');
    const connectCodForm = () => {
      const trigger = form.querySelector('#rsi_buy_now_button');
      form.classList.toggle('cod-app-connected', Boolean(trigger));
    };
    new MutationObserver(connectCodForm).observe(form, { childList: true, subtree: true });
    connectCodForm();
    buyNowButton?.addEventListener('click', () => {
      const trigger = form.querySelector('#rsi_buy_now_button');
      if (codError) codError.hidden = true;
      if (!trigger || trigger.disabled) {
        if (codError) codError.hidden = false;
        return;
      }
      trigger.click();
    });

    if (stickyBar && addButton) {
      const updateStickyBar = () => {
        const headerBottom = Math.max(0, document.querySelector('[data-site-header]')?.getBoundingClientRect().bottom || 0);
        const buttonBounds = addButton.getBoundingClientRect();
        const productBounds = root.getBoundingClientRect();
        const visible = window.innerWidth <= 860 && buttonBounds.bottom < headerBottom && productBounds.bottom > headerBottom + 90;
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.setAttribute('aria-hidden', String(!visible));
        stickyBar.toggleAttribute('inert', !visible);
        document.body.classList.toggle('product-sticky-atc-visible', visible);
      };
      updateStickyBar();
      window.addEventListener('scroll', updateStickyBar, { passive: true });
      window.addEventListener('resize', updateStickyBar);
    }

    /* Add through the AJAX API so the customer stays on the product page. */
    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const quantity = Math.max(1, Math.floor(Number(quantityInput && quantityInput.value) || 1));
      const isBuyNow = event.submitter?.hasAttribute('data-buy-now');
      const items = [
        {
          id: variantId(),
          quantity,
        },
      ];

      const addLabel = addButtonLabel ? addButtonLabel.textContent : root.dataset.addLabel;
      const buyNowLabel = buyNowButtonLabel ? buyNowButtonLabel.textContent : root.dataset.buyLabel;
      [addButton, buyNowButton, stickyButton, stickyBuyNowButton].forEach((button) => {
        if (button) button.disabled = true;
      });
      if (isBuyNow) {
        if (buyNowButtonLabel) buyNowButtonLabel.textContent = buyNowButton?.dataset.buying || buyNowLabel;
        if (stickyBuyNowButton) stickyBuyNowButton.textContent = buyNowButton?.dataset.buying || buyNowLabel;
      } else {
        if (addButtonLabel) addButtonLabel.textContent = addButton?.dataset.adding || addLabel;
        if (stickyButton) stickyButton.textContent = addButton?.dataset.adding || addLabel;
      }

      fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(isBuyNow ? {} : cartSectionRequest()) }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then((cart) => {
          if (isBuyNow) {
            window.location.assign(checkoutUrl);
            return undefined;
          }
          const html = cart.sections && cart.sections['cart-drawer'];
          if (renderCartDrawer(html)) return openCartDrawer();
          return refreshCartDrawer().then(() => openCartDrawer());
        })
        .catch(() => {
          if (addError) addError.hidden = false;
        })
        .finally(() => {
          [addButton, buyNowButton, stickyButton, stickyBuyNowButton].forEach((button) => {
            if (button) button.disabled = false;
          });
          if (addButtonLabel) addButtonLabel.textContent = addLabel;
          if (stickyButton) stickyButton.textContent = addLabel;
          if (buyNowButtonLabel) buyNowButtonLabel.textContent = buyNowLabel;
          if (stickyBuyNowButton) stickyBuyNowButton.textContent = buyNowLabel;
        });
    });

    update();
  });

  /* ----------------------------------------------------------------------
     4. Cart — removing a custom-size piece also removes its surcharge line
     ---------------------------------------------------------------------- */

  document.querySelectorAll('[data-remove-group]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const keys = (link.dataset.removeGroup || '').split(',').filter(Boolean);
      if (keys.length < 2) return; // nothing linked; let the href do its job

      event.preventDefault();
      link.setAttribute('aria-busy', 'true');

      keys
        .reduce(
          (chain, key) =>
            chain.then(() =>
              fetch(cartChangeUrl + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 }),
              })
            ),
          Promise.resolve()
        )
        .then(() => window.location.assign(cartUrl))
        .catch(() => window.location.assign(link.href));
    });
  });

  /* ----------------------------------------------------------------------
     5. Header — mark as scrolled so it can grow a hairline
     ---------------------------------------------------------------------- */

  const header = document.querySelector('[data-site-header]');
  if (header) {
    const headerSection = header.closest('.header-section');
    const announcement = headerSection?.querySelector('[data-announcement-slider]');
    const measureAnnouncement = () => {
      headerSection?.style.setProperty('--announcement-offset', `${announcement?.getBoundingClientRect().height || 0}px`);
    };
    if (announcement) new ResizeObserver(measureAnnouncement).observe(announcement);
    measureAnnouncement();
    let previousScroll = Math.max(0, window.scrollY);
    let scrollFrame;
    const setScrolled = () => {
      scrollFrame = null;
      const currentScroll = Math.max(0, window.scrollY);
      header.toggleAttribute('data-scrolled', currentScroll > 4);
      const interacting = header.contains(document.activeElement)
        || header.querySelector('details[open], [data-nav].is-open')
        || document.querySelector('[data-search-dialog][open]');
      if (currentScroll <= header.offsetHeight || interacting) {
        header.classList.remove('is-scroll-hidden');
        previousScroll = currentScroll;
        return;
      }
      if (Math.abs(currentScroll - previousScroll) >= 8) {
        header.classList.toggle('is-scroll-hidden', currentScroll > previousScroll);
        previousScroll = currentScroll;
      }
    };
    setScrolled();
    window.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(setScrolled);
    }, { passive: true });
    header.addEventListener('focusin', () => header.classList.remove('is-scroll-hidden'));
    window.addEventListener('resize', () => {
      header.classList.remove('is-scroll-hidden');
      previousScroll = Math.max(0, window.scrollY);
    });
  }

  /* ----------------------------------------------------------------------
     6. Scroll reveal — armed via a class so content is visible without JS
     ---------------------------------------------------------------------- */

  document.querySelectorAll('[data-product-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const previous = carousel.querySelector('[data-carousel-previous]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track || !previous || !next) return;

    const updateControls = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    const move = (direction) => {
      const card = track.querySelector('.product-card');
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls, { passive: true });
    updateControls();
  });

  const reveals = document.querySelectorAll('.reveal');
  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reveals.length && motionOk && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('reveal-on');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    reveals.forEach((el) => observer.observe(el));
  }
})();
